import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import type {z} from 'zod';
import {
  InternalPreviewCodeQueryParam,
  PreviewCodeQueryParam,
  type RecruitmentDetailResponse,
} from '../../../core/entities/recruitment/schema';
import {
  getRecruitmentDetail,
  getRecruitmentIdByDetailId,
} from '../../../core/repositories/recruitmentRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {PreviewModeTag} from '../../../features/recruitment/components/Container/Presenter/PreviewModeTag';
import {RecruitmentDetailLayout} from '../../../features/recruitment/components/RecruitmentDetailLayout';
import {ViewPage} from '../../../features/recruitment/components/ViewPage';
import {filterInternalPreviewCodeParams} from '../../../features/recruitment/usecases/filterInternalPreviewCodeParams';
import {handlePreviewError} from '../../../features/recruitment/usecases/handlePreviewError';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  recruitmentDetailId: number;
  recruitmentItemId: number;
  recruitmentDetail: RecruitmentDetailResponse;
};

const QueryParameter = PreviewCodeQueryParam.merge(
  InternalPreviewCodeQueryParam,
);
type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    const detailId = Number(context.params?.id);
    const {
      preview_code,
      internal_preview_code,
      sex,
      bun_ri,
      university,
      current_univ_type,
    } = context.validatedQuery;

    const getRecruitmentItemIdResponse = await getRecruitmentIdByDetailId({
      cookies,
      presenter: 'client',
      detailId,
    });
    if (!getRecruitmentItemIdResponse.ok) {
      if (getRecruitmentItemIdResponse.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(getRecruitmentItemIdResponse.error);
    }

    const internal_previewCodeParams = filterInternalPreviewCodeParams({
      internal_preview_code,
      sex,
      bun_ri,
      university,
      current_univ_type,
    });
    const {recruitmentID: recruitmentItemId} =
      getRecruitmentItemIdResponse.value;
    const getRecruitmentDetailResponse = await getRecruitmentDetail({
      cookies,
      recruitmentID: recruitmentItemId,
      preview_code: preview_code ?? undefined,
      ...internal_previewCodeParams,
    });

    const previewError = handlePreviewError(getRecruitmentDetailResponse);
    if (previewError) {
      return {props: previewError as any};
    }

    if (!getRecruitmentDetailResponse.ok) {
      if (getRecruitmentDetailResponse.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(getRecruitmentDetailResponse.error);
    }

    if (!getRecruitmentDetailResponse.value) {
      return {
        notFound: true,
      };
    }

    const props: Props = {
      recruitmentDetailId: detailId,
      recruitmentItemId,
      recruitmentDetail: getRecruitmentDetailResponse.value,
    };

    if (!isValid) {
      return {props};
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  recruitmentDetailId,
  recruitmentItemId,
  userDataForAnalytics,
  recruitmentDetail,
}: Props) => {
  return (
    <>
      {recruitmentDetail.isPreview && (
        <div className="pc:p-0 px-8">
          <PreviewModeTag />
        </div>
      )}
      <ViewPage
        recruitmentDetailId={recruitmentDetailId}
        recruitmentDetail={recruitmentDetail}
        recruitmentItemId={recruitmentItemId}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => (
  <RecruitmentDetailLayout>{page}</RecruitmentDetailLayout>
);
